import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowUturnLeftIcon, CheckCircleIcon, DocumentArrowDownIcon, EnvelopeIcon, EyeIcon, PrinterIcon, XCircleIcon } from '@heroicons/react/24/outline';
import {
  getGraduationById,
  getGraduationDepositPayments,
  getGraduationDepositPaymentById,
  approveGraduationDepositPayment,
  rejectGraduationDepositPayment,
  reverseGraduationDepositPayment,
  printGraduationDepositReceipt,
  emailGraduationDepositReceipt,
} from '../../../../services/crmApi';
import LoadingSpinner from '../../../Common/LoadingSpinner';
import ConfirmationModal from '../../../Common/ConfirmationModal';
import SearchableLedgerDropdown from '../../../Common/SearchableLedgerDropdown';
import { paymentSettingsApi } from '../../../../services/financeApi';
import {
  findFinancePaymentMethodMapping,
  formatFinanceSourceAccountLabel,
  extractFinanceSourceAccountId,
  defaultApproveReceivedDateTime,
  buildApproveFinancePayload
} from '../../../../utils/crmPaymentFinanceDefaults';

const statusColors = {
  Pending: 'bg-yellow-100 text-yellow-800',
  Approved: 'bg-green-100 text-green-800',
  Rejected: 'bg-red-100 text-red-800',
  Reversed: 'bg-gray-100 text-gray-800'
};

const StaffGraduationDepositReview = () => {
  const { id } = useParams();
  const [graduation, setGraduation] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [actualAmount, setActualAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [approveReceivedDate, setApproveReceivedDate] = useState('');
  const [approveGlSourceAccountId, setApproveGlSourceAccountId] = useState('');
  const [approveGlSourceAccountLabel, setApproveGlSourceAccountLabel] = useState('');
  const [emailReceiptPaymentId, setEmailReceiptPaymentId] = useState(null);
  const [emailingReceipt, setEmailingReceipt] = useState(false);

  useEffect(() => {
    fetchGraduation();
    fetchPayments();
  }, [id]);

  const fetchGraduation = async () => {
    try {
      const response = await getGraduationById(id);
      setGraduation(response.data?.data || null);
    } catch (error) {
      console.error('Error fetching graduation:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await getGraduationDepositPayments(id, { limit: 200 });
      setPayments(response.data.data.payments || []);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const openApprove = async (payment) => {
    setSelected(payment);
    setActualAmount(String(payment.amount));
    setNotes('');
    setApproveReceivedDate(defaultApproveReceivedDateTime(payment));
    setApproveGlSourceAccountId('');
    setApproveGlSourceAccountLabel('');
    try {
      const settingsRes = await paymentSettingsApi.getSettings({ skipErrorToast: true });
      const settings = settingsRes.data?.data || settingsRes.data || {};
      const locationId = graduation?.locationId?._id || graduation?.locationId || null;
      const mapping = findFinancePaymentMethodMapping(settings, payment.paymentMethod, locationId)
        || findFinancePaymentMethodMapping(settings, payment.paymentMethod, null);
      const defaultId = extractFinanceSourceAccountId(mapping);
      if (defaultId) {
        setApproveGlSourceAccountId(defaultId);
        setApproveGlSourceAccountLabel(formatFinanceSourceAccountLabel(mapping));
      }
    } catch {
      /* leave COA empty */
    }
  };

  const handleApprove = async () => {
    const missing = [];
    if (!graduation?.deposit?.liabilityAccountId) missing.push('Deposit Liability Account');
    if (!graduation?.deposit?.incomeAccountId) missing.push('Forfeiture Income Account');
    if (missing.length > 0) {
      toast.error(`Cannot approve: configure ${missing.join(' and ')} in the graduation form first.`);
      return;
    }
    try {
      const res = await approveGraduationDepositPayment(
        selected._id,
        Number(actualAmount),
        notes,
        buildApproveFinancePayload({
          receivedDate: approveReceivedDate,
          glSourceAccountId: approveGlSourceAccountId
        })
      );
      const receiptNumber = res?.data?.data?.receiptNumber;
      toast.success(receiptNumber
        ? `Payment approved — receipt ${receiptNumber} issued`
        : 'Payment approved');
      setSelected(null);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve');
    }
  };

  const downloadDepositReceipt = async (paymentId, { variant } = {}) => {
    try {
      const res = await printGraduationDepositReceipt(paymentId, { variant });
      const payment = (payments || []).find((p) => String(p._id) === String(paymentId));
      const base = String(payment?.receiptNumber || `receipt-${paymentId}`).replace(/[/\\]/g, '-');
      const suffix = variant === 'preprinted' ? '.preprinted.pdf' : '.pdf';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${base}${suffix}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(variant === 'preprinted' ? 'Pre-printed receipt downloaded' : 'Receipt PDF downloaded');
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to download receipt');
    }
  };

  const requestEmailDepositReceipt = (paymentId) => {
    if (!paymentId) return;
    setEmailReceiptPaymentId(paymentId);
  };

  const handleEmailDepositReceipt = async (paymentId) => {
    try {
      setEmailingReceipt(true);
      const res = await emailGraduationDepositReceipt(paymentId);
      toast.success(res.data?.message || 'Receipt email is being sent');
      setEmailReceiptPaymentId(null);
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to email receipt');
    } finally {
      setEmailingReceipt(false);
    }
  };

  const handleReject = async (paymentId) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    try {
      await rejectGraduationDepositPayment(paymentId, reason);
      toast.success('Payment rejected');
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject');
    }
  };

  const handleReverse = async (paymentId) => {
    const reason = window.prompt('Reversal reason:');
    if (!reason) return;
    try {
      await reverseGraduationDepositPayment(paymentId, reason);
      toast.success('Payment reversed');
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reverse');
    }
  };

  const viewDetails = async (paymentId) => {
    try {
      const response = await getGraduationDepositPaymentById(paymentId);
      const payment = response.data.data;
      alert(`
Student: ${payment.studentId?.fullName || 'N/A'}
Amount: ${payment.amount} ${payment.currency}
Paid: ${payment.paidAmount || 0}
Credit: ${payment.creditAmount || 0}
Method: ${payment.paymentMethod}
Reference: ${payment.referenceNumber || 'N/A'}
Bank: ${payment.bankName || 'N/A'} ${payment.branchName || ''}
Notes: ${payment.notes || 'N/A'}
GL Posted: ${payment.glPosted ? 'Yes' : 'No'}
      `.trim());
    } catch (error) {
      toast.error('Failed to load payment details');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Review Graduation Deposit Payments</h1>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Student</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Paid</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Method</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{payment.studentId?.fullName || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{payment.amount} {payment.currency}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{payment.paidAmount || 0}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{payment.paymentMethod}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{payment.referenceNumber || '-'}
                    {payment.receiptNumber && payment.approvedAt ? (
                      <div className="text-[11px] text-gray-500">Receipt Date (Approved): {new Date(payment.approvedAt).toLocaleString()}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${statusColors[payment.status] || 'bg-gray-100'}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-0.5">
                      <button type="button" title="View" aria-label="View" onClick={() => viewDetails(payment._id)} className="p-1.5 rounded transition-all text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50">
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      {payment.status === 'Pending' && (
                        <>
                          <button type="button" title="Approve" aria-label="Approve" onClick={() => openApprove(payment)} className="p-1.5 rounded transition-all text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50">
                            <CheckCircleIcon className="w-4 h-4" />
                          </button>
                          <button type="button" title="Reject" aria-label="Reject" onClick={() => handleReject(payment._id)} className="p-1.5 rounded transition-all text-red-600 hover:text-red-800 hover:bg-red-50">
                            <XCircleIcon className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {Boolean(payment.receiptNumber) && (
                        <>
                          <button type="button" title="Download digital receipt PDF" aria-label="Download digital receipt PDF" onClick={() => downloadDepositReceipt(payment._id)} className="p-1.5 rounded transition-all text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50">
                            <DocumentArrowDownIcon className="w-4 h-4" />
                          </button>
                          <button type="button" title="Download pre-printed form PDF" aria-label="Download pre-printed form PDF" onClick={() => downloadDepositReceipt(payment._id, { variant: 'preprinted' })} className="p-1.5 rounded transition-all text-slate-600 hover:text-slate-800 hover:bg-slate-50">
                            <PrinterIcon className="w-4 h-4" />
                          </button>
                          <button type="button" title="Email digital receipt" aria-label="Email digital receipt" onClick={() => requestEmailDepositReceipt(payment._id)} className="p-1.5 rounded transition-all text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50">
                            <EnvelopeIcon className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {payment.status === 'Approved' && (
                        <button type="button" title="Reverse" aria-label="Reverse" onClick={() => handleReverse(payment._id)} className="p-1.5 rounded transition-all text-amber-600 hover:text-amber-800 hover:bg-amber-50">
                          <ArrowUturnLeftIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Approve Deposit Payment</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Actual Amount</label>
                <input
                  type="number"
                  value={actualAmount}
                  onChange={(e) => setActualAmount(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Receiving GL account (COA)</label>
                <SearchableLedgerDropdown
                  value={approveGlSourceAccountId}
                  displayLabel={approveGlSourceAccountLabel}
                  onChange={(accountId, displayLabel) => {
                    setApproveGlSourceAccountId(accountId || '');
                    setApproveGlSourceAccountLabel(displayLabel || '');
                  }}
                  placeholder="Select chart of accounts"
                  menuPlacement="up"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Payment received date &amp; time</label>
                <input
                  type="datetime-local"
                  value={approveReceivedDate}
                  onChange={(e) => setApproveReceivedDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  rows="3"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button onClick={() => setSelected(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-medium">Cancel</button>
                <button onClick={handleApprove} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">Approve</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={Boolean(emailReceiptPaymentId)}
        title="Email digital receipt"
        message={(() => {
          const payment = (payments || []).find((p) => String(p._id) === String(emailReceiptPaymentId));
          const email = (
            payment?.studentId?.email
            || payment?.student?.email
            || payment?.studentEmail
            || ''
          ).trim();
          const receiptNo = payment?.receiptNumber || 'this payment';
          return email
            ? `Send the digital sales receipt (${receiptNo}) to ${email}?`
            : `Send the digital sales receipt (${receiptNo}) to the student email on file?`;
        })()}
        confirmText="Send Email"
        type="info"
        loading={emailingReceipt}
        onConfirm={() => handleEmailDepositReceipt(emailReceiptPaymentId)}
        onClose={() => !emailingReceipt && setEmailReceiptPaymentId(null)}
      />
    </div>
  );
};

export default StaffGraduationDepositReview;
